import { Box, Divider, Typography } from '@mui/material';
import { PsychiatryDischargeNoteCourseOfTreatment } from '@uplift/api';
import { CourseOfTreatmentReviewSection } from './CourseOfTreatmentReviewSection.component';
import { DischargeNoteReviewSection, DischargeNoteReviewSubSection } from './ReviewSections.component';
import { Fragment } from 'react';
import { useAppointmentAssessmentsBySubmissionIds } from '@uplift/queries';
import { AppointmentAssessmentChip, Markdown, MedicationItem } from '@uplift/ui-v2';

export type PsychiatryDischargeNoteCourseOfTreatmentSectionsProps = {
  courseOfTreatment: PsychiatryDischargeNoteCourseOfTreatment;
  isSidebar?: boolean;
};

export const PsychiatryDischargeNoteCourseOfTreatmentSections: React.VFC<PsychiatryDischargeNoteCourseOfTreatmentSectionsProps> = props => {
  const { courseOfTreatment } = props;

  const {
    initialDiagnosis = [],
    chiefComplaint,
    psychiatricReviewOfSystemsNote,
    medicationHistory = [],
    dischargeMedication = [],
    initialAssessmentSubmissionIds = [],
    psychAssessments = [],
  } = courseOfTreatment;

  const { data: initialAssessments = [] } = useAppointmentAssessmentsBySubmissionIds(initialAssessmentSubmissionIds);

  return (
    <Fragment>
      <CourseOfTreatmentReviewSection {...courseOfTreatment} isSidebar={props.isSidebar} />

      <Divider />

      <DischargeNoteReviewSection title="Initial Evaluation">
        <DischargeNoteReviewSubSection title="Chief Complaint" isEmpty={!chiefComplaint} emptyMessage="Not Applicable">
          <Box my={-2}>
            <Markdown>{chiefComplaint}</Markdown>
          </Box>
        </DischargeNoteReviewSubSection>
        <DischargeNoteReviewSubSection title="Psychiatric Review of Systems" isEmpty={!psychiatricReviewOfSystemsNote} emptyMessage="Not Applicable">
          <Box my={-2}>
            <Markdown>{psychiatricReviewOfSystemsNote}</Markdown>
          </Box>
        </DischargeNoteReviewSubSection>
        <DischargeNoteReviewSubSection title="Assessments" isEmpty={!initialAssessments.length} emptyMessage="Did not complete">
          <Box display="flex" flexDirection="column" gap="8px">
            <Box display="flex" gap="16px" flexWrap="wrap">
              {initialAssessments.map((a, idx) => <AppointmentAssessmentChip appointmentAssessment={a} key={idx} />)}
            </Box>
            {psychAssessments.map((a, idx) => (
              <Typography key={idx}>Provider administered <span style={{ fontWeight: 500 }}>{a.name}</span> during session. Results: {a.result}</Typography>
            ))}
          </Box>
        </DischargeNoteReviewSubSection>
        <DischargeNoteReviewSubSection title="Initial Diagnosis" isEmpty={!initialDiagnosis.length} emptyMessage="Not Applicable">
          <Box display="flex" flexDirection="column" gap="16px">
            {initialDiagnosis.map((d) => (
              <Typography key={d.id}>{d.code} {d.name}</Typography>
            ))}
          </Box>
        </DischargeNoteReviewSubSection>
      </DischargeNoteReviewSection>

      <Divider />

      <DischargeNoteReviewSection title="Medication">
        <DischargeNoteReviewSubSection title="Uplift Prescriptions" isEmpty={!medicationHistory.length} emptyMessage="Not Applicable">
          <div>
            {medicationHistory.map((m, idx) => <MedicationItem medication={m} key={idx} />)}
          </div>
        </DischargeNoteReviewSubSection>
        <DischargeNoteReviewSubSection title="Discharge Medication" isEmpty={!dischargeMedication.length} emptyMessage="Not Applicable">
          <div>
            {dischargeMedication.map((m, idx) => <MedicationItem medication={m} key={idx} />)}
          </div>
        </DischargeNoteReviewSubSection>
      </DischargeNoteReviewSection>

      <Divider />

    </Fragment>
  );
};
